import type { PersonnelDetail, UpdatePersonnelRequest } from '../personnelTypes';

/**
 * Everything `PUT /api/personnel/{id}` needs, taken from the record already on screen — and
 * **no identity fields at all**.
 *
 * The update endpoint replaces what it is sent, so a modal that changes one section still has
 * to send every other section back unchanged. Deriving that from the loaded record in ONE
 * place is what stops five section modals each growing their own copy of the mapping: a field
 * added to the record is added here, and every section modal keeps sending it. A modal that
 * built its own payload would silently blank whatever it forgot.
 *
 * **The identity block is deliberately absent**, which is the whole reason a section modal can
 * exist. `firstName`, `middleName`, `lastName`, `suffix` and `dateOfBirth` live in `rsys_pii`,
 * the profile page never loads them (they are `personnel.reveal`, not `read`), and the server
 * leaves the identity row alone when they are not sent. Note that `middleName: null` would NOT
 * be neutral — the server reads any present key as "the caller is changing the identity" and
 * then demands the whole block — so the keys are omitted rather than nulled.
 *
 * A section modal spreads this and overrides only its own fields:
 *
 * ```ts
 * personnelApi.update(record.id, { ...toUpdateRequest(record), addresses: next })
 * ```
 */
export function toUpdateRequest(record: PersonnelDetail): UpdatePersonnelRequest {
  return {
    sex: record.sex,
    civilStatus: record.civilStatus ?? null,
    employmentType: record.employmentType ?? null,
    contactNumber: record.contactNumber ?? null,
    email: record.email ?? null,
    userId: record.userId ?? null,
    dateHired: record.dateHired,
    bloodTypeId: record.bloodTypeId,
    nationalityId: record.nationalityId,
    religionId: record.religionId,
    hmoProviderId: record.hmoProviderId,
    hmoCardNumber: record.hmoCardNumber,

    /*
      THE NUMBER IS SENT AS null, WHICH MEANS "LEAVE IT ALONE".

      This helper rebuilds the whole update request from the record so that editing ONE section
      does not blank the others — and since 17 Sep 2026 the record no longer carries the
      numbers, because they live in rsys_pii behind personnel.reveal. Sending anything else
      would be catastrophic in a quiet way: an empty string means "remove", so correcting a
      phone number on another card would wipe every government ID the employee holds.

      The identification editor is the one form that sends real values, and it reveals them
      first.
    */
    identifications: record.identifications.map((value) => ({
      identificationTypeId: value.identificationTypeId,
      number: null,
      placeIssued: value.placeIssued ?? null,
      dateIssued: value.dateIssued ?? null,
      validUntil: value.validUntil ?? null,
    })),

    isMilitary: record.isMilitary,
    militaryBranch: record.militaryBranch,
    militaryServiceStatus: record.militaryServiceStatus,
    militaryRank: record.militaryRank,
    militaryServiceNumber: record.militaryServiceNumber,
    militaryUnit: record.militaryUnit,
    militaryDateEnteredService: record.militaryDateEnteredService,
    militaryDateSeparated: record.militaryDateSeparated,
    isPwd: record.isPwd,
    pwdIdNumber: record.pwdIdNumber,
    permanentAddressSameAsPresent: record.permanentAddressSameAsPresent,

    // `id` is the stored row's, not part of the input — sending it back would be a field the
    // request has no property for.
    addresses: record.addresses.map((address) => ({
      kind: address.kind,
      streetAddress: address.streetAddress,
      barangay: address.barangay,
      cityMunicipality: address.cityMunicipality,
      province: address.province,
      zipCode: address.zipCode,
    })),

    /*
      The account is linked and unlinked through its own step on the create form and through
      `userId` above; there is no account block on an update, and the profile's Account card
      links out to the user's own record rather than editing it here.
    */
    account: null,
  };
}
